import React, { useMemo } from 'react'
import type {
    CategoryInterface,
    ProductInterface,
    CmsPage,
    Maybe
} from '@pylot-data/pylotschema'
import { useRouter } from 'next/router'
import { Product } from './Product'
import { Category } from './Category'
import { CMSPage } from './CMSPage'
import { Breadcrumbs } from './Breadcrumbs'
import { SRP } from './Srp'
import { BaseSeo } from './BaseSeo'
import Head from 'next/head'
import { PAGE_TYPE } from './enums'
import { getCanonical } from './util/getCanonical'
import { PreloadImages } from './Preload/PreloadImages'
const corePages = [PAGE_TYPE.PLP, PAGE_TYPE.PDP, PAGE_TYPE.CMS, PAGE_TYPE.HOME]

type Page = PAGE_TYPE

export type SEOProps = {
    product?: Maybe<ProductInterface>
    cmsPage?: Maybe<CmsPage>
    category?: Maybe<CategoryInterface>
    titleOverride?: string
    descriptionOverride?: string
    disableCanonicals?: boolean
    noFollow?: boolean
    page?: Page
    products?: Maybe<Array<ProductInterface>>
    customPath?: Maybe<string>
    preloadImages?: {
        images: string[]
        quality?: number
        imageFormat?: string
        auto?: string
    }
}

export const SEO = ({
    product,
    cmsPage,
    category,
    titleOverride,
    descriptionOverride,
    disableCanonicals,
    noFollow,
    products,
    page = PAGE_TYPE.OTHER,
    customPath,
    preloadImages
}: SEOProps): JSX.Element => {
    const { asPath, locale } = useRouter()
    const pathname = String(customPath || asPath).replace(/^\//, '')
    const canonicalUrl = !disableCanonicals
        ? getCanonical(pathname, locale, !!customPath)
        : undefined

    const isProd = Number(process.env.NEXT_PUBLIC_PROD) == 1

    const metaRobotsContent = isProd ? 'index,follow' : 'noindex,nofollow'

    const preloadImagesMetatags = useMemo(
        () =>
            preloadImages && (
                <PreloadImages optimizeBlockImages={preloadImages} />
            ),

        [
            preloadImages,
            asPath,
            category,
            cmsPage,
            disableCanonicals,
            descriptionOverride,
            noFollow,
            pathname,
            canonicalUrl,
            product,
            titleOverride
        ]
    )

    const baseSeo = useMemo(
        () =>
            page === PAGE_TYPE.OTHER && (
                <BaseSeo
                    titleOverride={titleOverride}
                    descriptionOverride={descriptionOverride}
                    asPath={asPath}
                    canonicalUrl={canonicalUrl}
                    noFollow={noFollow}
                />
            ),
        [
            asPath,
            category,
            cmsPage,
            disableCanonicals,
            descriptionOverride,
            noFollow,
            pathname,
            canonicalUrl,
            product,
            titleOverride
        ]
    )

    const breadcrumbs = useMemo(
        () =>
            corePages.indexOf(page) >= 0 && (
                <Breadcrumbs
                    product={product}
                    cmsPage={cmsPage}
                    category={category}
                    titleOverride={titleOverride}
                    canonicalUrl={canonicalUrl!}
                />
            ),
        [pathname]
    )

    const productSeo = useMemo(
        () =>
            page == PAGE_TYPE.PDP &&
            product && (
                <Product
                    product={product}
                    titleOverride={titleOverride}
                    descriptionOverride={descriptionOverride}
                    canonicalUrl={canonicalUrl!}
                />
            ),
        [
            page,
            descriptionOverride,
            pathname,
            canonicalUrl,
            product,
            titleOverride
        ]
    )

    const cmsPageSeo = useMemo(
        () =>
            (page === PAGE_TYPE.CMS || page === PAGE_TYPE.HOME) &&
            cmsPage && (
                <CMSPage
                    page={page}
                    cmsPage={cmsPage}
                    titleOverride={titleOverride}
                    descriptionOverride={descriptionOverride}
                    canonicalUrl={canonicalUrl!}
                    asPath={asPath}
                />
            ),
        [
            page,
            cmsPage,
            descriptionOverride,
            canonicalUrl,
            pathname,
            titleOverride
        ]
    )

    const categorySeo = useMemo(
        () =>
            page === PAGE_TYPE.PLP &&
            category && (
                <Category
                    category={category}
                    titleOverride={titleOverride}
                    descriptionOverride={descriptionOverride}
                    canonicalUrl={canonicalUrl!}
                    products={products}
                />
            ),
        [
            page,
            category,
            descriptionOverride,
            canonicalUrl,
            pathname,
            titleOverride,
            products
        ]
    )

    const srpSeo = useMemo(
        () => page === PAGE_TYPE.SRP && <SRP title={titleOverride} />,
        [page, titleOverride]
    )

    return (
        <>
            {preloadImagesMetatags}
            {baseSeo}
            {breadcrumbs}
            {productSeo}
            {cmsPageSeo}
            {categorySeo}
            {srpSeo}
            {page !== PAGE_TYPE.SRP && (
                <Head>
                    <meta name="robots" content={metaRobotsContent} />
                </Head>
            )}
        </>
    )
}
